import React, { useEffect, useRef } from "react";

import { PillsDialogContent } from "../components/pills-dialog-content";
import { useDialog } from "../context/messaging-layer";
import {
  convertDateAndTimeToDate,
  dateToOnlyTimeString,
  dateToOnlyDateString,
  formatDateAndTimeToUTCString,
  convertToUtc,
  getAdjustedMinDate
} from "../utils/date-time-helper";
import { dateTimeFieldValidationUpdate } from "../utils/cigna-date-time-picker-helper";

import fieldPropTypes from "./field-prop-types";
import ValidationMessage from "../theme/default/validation-message";

const DateTimePicker = ({
  field: { name: fieldName, action, params, runFieldAction },
  appData: {
    handleChange,
    values,
    resetFieldValidation,
    dateTimeSelection,
    setSelectedDate,
    setSelectedTime,
    settings,
    screenValidation,
    fieldValidation: fieldValidationFromRedux = {}
  },
  components,
  appData,
  field
}) => {
  const wrapperRef = useRef(null);
  const {
    selectedDate,
    selectedTime,
    availableTimes,
    isLoading: areTimesLoading,
    error: errorMessage,
    isLoaded: areTimesLoaded
  } = dateTimeSelection;
  const { Label, DatePicker, Input } = components;
  const { [fieldName]: dateTime, time_zone } = values;
  const isRequired = !params.includes("TDFieldOptionIsOptional");
  /*
    #TODO: [CIGNA] Change the following harcode texts with server data
  */
  const localData = {
    date: {
      labelName: `${fieldName}_date`,
      labelText: settings.translations.date,
      placeholderText: settings.translations.select_prompt,
      fieldName: settings.translations.date
    },
    time: {
      labelName: `${fieldName}_time`,
      labelText: settings.translations.time,
      placeholderText: settings.translations.select_prompt,
      fieldName: settings.translations.time
    }
  };

  const { showDialog } = useDialog();

  useEffect(() => {
    if (runFieldAction) {
      runFieldAction();
    }
  }, []);

  useEffect(() => {
    dateTimeFieldValidationUpdate({
      field,
      appData,
      dateValue: selectedDate,
      timeValue: selectedTime,
      dateFieldName: localData.date.fieldName,
      timeFieldName: localData.time.fieldName,
      fieldRef: wrapperRef
    });
  }, [screenValidation, selectedTime, selectedDate]);

  const getValidationField = () => fieldValidationFromRedux?.[fieldName];

  const isInvalidLocalField = localFieldName => {
    const validationField = getValidationField();

    return (
      !!validationField && !validationField.isLocalFieldValid[localFieldName]
    );
  };

  const getInvalidErrorMessage = localFieldName =>
    getValidationField()?.errorMessage?.[localFieldName] ||
    settings.translations.undefined_error;

  const handleTimeChange = inputTime => {
    const time = dateToOnlyTimeString(inputTime);

    if (time !== selectedTime) {
      resetFieldValidation(fieldName);
      setSelectedTime(time);
      handleChange({
        [fieldName]: formatDateAndTimeToUTCString(selectedDate, time, time_zone)
      });
    }
  };

  const handleDateChange = inputDate => {
    const formattedInputDate = dateToOnlyDateString(inputDate);

    setSelectedDate(formattedInputDate);

    if (action.type === "url") {
      const utcDate = convertToUtc(formattedInputDate, time_zone);

      const newData = {
        date: utcDate
      };

      // We should update+apply validation in case the 'Time' field is INVALID,
      // otherwise just reset entire fieldValidation
      if (isInvalidLocalField(localData.time.fieldName)) {
        dateTimeFieldValidationUpdate({
          field,
          appData,
          dateValue: utcDate,
          timeValue: "",
          dateFieldName: localData.date.fieldName,
          timeFieldName: localData.time.fieldName,
          needImmediateApply: true,
          fieldRef: wrapperRef
        });
      } else {
        resetFieldValidation(fieldName);
      }

      handleChange(newData);

      if (runFieldAction) {
        runFieldAction(newData);
      }
    }
  };

  const handleTimepickerClick = e => {
    e.preventDefault();

    const mappedAvailableTimes = availableTimes.map(aTime =>
      convertDateAndTimeToDate(selectedDate, aTime)
    );
    const selectedTimeValue =
      selectedDate && selectedTime
        ? convertDateAndTimeToDate(selectedDate, selectedTime)
        : null;

    /*
      #TODO: [CIGNA] Change this harcode dialogTitle with server data
    */
    const dialogTitle = !areTimesLoaded
      ? settings.translations.select_date_first
      : !errorMessage
      ? settings.translations.pick_a_time
      : errorMessage;

    showDialog({
      actions: [
        {
          label: settings.translations.continue,
          name: "close-timepicker"
        }
      ],
      content: {
        title: dialogTitle,
        children: (
          <PillsDialogContent
            value={selectedTimeValue}
            invalid={isInvalidLocalField(localData.time.fieldName)}
            availableTimes={mappedAvailableTimes}
            handleTimeChange={handleTimeChange}
            components={components}
          />
        )
      }
    });
  };

  const delay =
    (typeof action.data?.allow_schedule_delay !== "object"
      ? action.data?.allow_schedule_delay
      : action.data?.allow_schedule_delay?.delay) || 0;
  const minDate = getAdjustedMinDate(dateTime, delay, params);

  const dateValue = selectedDate ? new Date(selectedDate) : null;
  const isDatePickerInputFieldClearable = true;

  return (
    <div className='date-time-picker-wrapper-web-sdk' ref={wrapperRef}>
      <section>
        <Label
          name={localData.date.labelName}
          text={localData.date.labelText}
          isRequired={isRequired}
          appData={appData}
          params={params}
          srLabel={localData.date.labelText}
        />
        <DatePicker
          isClearable={isDatePickerInputFieldClearable}
          value={dateValue}
          minDate={minDate}
          onChange={handleDateChange}
          placeholderText={localData.date.placeholderText}
          components={components}
          invalid={isInvalidLocalField(localData.date.fieldName)}
          showCalendarIcon
          appData={appData}
        />

        <ValidationMessage
          isInvalid={isInvalidLocalField(localData.date.fieldName)}
        >
          {getInvalidErrorMessage(localData.date.fieldName)}
        </ValidationMessage>
      </section>

      <section>
        <Label
          name={localData.time.labelName}
          text={localData.time.labelText}
          isRequired={isRequired}
          appData={appData}
          params={params}
          srLabel={localData.time.labelText}
        />
        <Input
          value={selectedTime}
          onClick={handleTimepickerClick}
          name={localData.time.fieldName}
          placeholder={localData.time.placeholderText}
          disabled={areTimesLoading}
          onChange={e => {
            e.preventDefault();
          }}
          invalid={isInvalidLocalField(localData.time.fieldName)}
          useTimeIcon
          showLoadingSpiner={areTimesLoading}
        />

        <ValidationMessage
          isInvalid={isInvalidLocalField(localData.time.fieldName)}
        >
          {getInvalidErrorMessage(localData.time.fieldName)}
        </ValidationMessage>
      </section>
    </div>
  );
};

DateTimePicker.propTypes = fieldPropTypes;

export default DateTimePicker;
